// What a partner reporter sees of its runs: the intro, the sort choice, the month
// and day counts and the collapsible run list. Shared by the real Meldingen page
// and the customer view on the Runs page, so the two cannot drift apart.
import { useMemo, useState, type ReactNode } from 'react';
import type { BillingRun, ReporterRun } from '../lib/runs';
import { formatVenueDate, formatVenueTime } from '../lib/time';

// Newest first by default: the run they want to report is usually the one
// that just happened. The choice sticks per device.
export type SortOrder = 'newest' | 'oldest';
const SORT_STORAGE_KEY = 'neoseum-admin-meldingen-sort';

function readSortOrder(): SortOrder {
  try {
    return window.localStorage.getItem(SORT_STORAGE_KEY) === 'oldest' ? 'oldest' : 'newest';
  } catch {
    return 'newest';
  }
}

export function useReportSort(): [SortOrder, (next: SortOrder) => void] {
  const [sort, setSort] = useState<SortOrder>(() => readSortOrder());
  function changeSort(next: SortOrder) {
    setSort(next);
    try {
      window.localStorage.setItem(SORT_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }
  return [sort, changeSort];
}

export function ReportIntro() {
  return (
    <p className="report-intro muted">
      Ging een run fout (bezoeker opnieuw gestart, headset uitgevallen, spel vastgelopen)? Meld het
      bij die run, met een korte omschrijving en eventueel een foto. De run wordt dan niet
      gefactureerd. Neoseum controleert meldingen achteraf; een afwijzing zie je hier terug.
    </p>
  );
}

export function ReportSortField({
  sort,
  onChange,
}: {
  sort: SortOrder;
  onChange: (next: SortOrder) => void;
}) {
  return (
    <label className="walkin-field payments-filter">
      <span>Volgorde</span>
      <select value={sort} onChange={(e) => onChange(e.target.value as SortOrder)}>
        <option value="newest">Nieuwste eerst</option>
        <option value="oldest">Oudste eerst</option>
      </select>
    </label>
  );
}

// A fault is a run with an accepted report. These counts come from the reporter's
// own data, which does not include a void a manager set by hand without a report,
// so "zonder fout" is not the invoice count and must not be labelled as one.
function countFaults(rows: ReporterRun[]): number {
  return rows.filter((r) => r.report?.status === 'accepted').length;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

// The server returns oldest first; flip the whole list for newest first so
// days and the runs within a day both follow the chosen order. renderBody fills
// the part below each run: the report, or what the viewer can do about it.
//
// Only the most recent day starts open, because that is the one people report
// on. A closed day stays mounted, so a half-written report survives collapsing.
export function ReporterRunDays({
  runs,
  sort,
  renderBody,
}: {
  runs: ReporterRun[];
  sort: SortOrder;
  renderBody: (run: ReporterRun) => ReactNode;
}) {
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  const grouped = useMemo(() => {
    const ordered = [...runs];
    if (sort === 'newest') ordered.reverse();
    const out: { day: string; rows: ReporterRun[] }[] = [];
    for (const r of ordered) {
      const day = formatVenueDate(r.started_at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push(r);
      else out.push({ day, rows: [r] });
    }
    return out;
  }, [runs, sort]);

  if (grouped.length === 0) return null;

  const latestDay = (sort === 'newest' ? grouped[0] : grouped[grouped.length - 1])?.day;
  const totalFaults = countFaults(runs);

  return (
    <>
      <div className="report-summary">
        <div className="report-summary-tile">
          <span>Runs</span>
          <strong>{runs.length}</strong>
        </div>
        <div className="report-summary-tile">
          <span>Fouten</span>
          <strong>{totalFaults}</strong>
        </div>
        <div className="report-summary-tile">
          <span>Zonder fout</span>
          <strong>{runs.length - totalFaults}</strong>
        </div>
      </div>

      <div className="report-day-grid report-days-head" aria-hidden="true">
        <span />
        <span>Dag</span>
        <span className="report-day-num">Runs</span>
        <span className="report-day-num">Fouten</span>
        <span className="report-day-num">Zonder fout</span>
      </div>

      {grouped.map((g) => {
        const open = toggled[g.day] ?? g.day === latestDay;
        const faults = countFaults(g.rows);
        const clean = g.rows.length - faults;
        return (
          <section key={g.day} className="report-day">
            <h2 className="report-day-title">
              <button
                type="button"
                className="report-day-grid report-day-row"
                aria-expanded={open}
                aria-label={`${g.day}: ${plural(g.rows.length, 'run', 'runs')}, ${plural(faults, 'fout', 'fouten')}, ${clean} zonder fout`}
                onClick={() => setToggled((cur) => ({ ...cur, [g.day]: !open }))}
              >
                <span className={open ? 'report-day-chevron report-day-chevron-open' : 'report-day-chevron'}>▸</span>
                <span className="report-day-date">{g.day}</span>
                <span className="report-day-num">{g.rows.length}</span>
                <span className={faults > 0 ? 'report-day-num report-day-faults' : 'report-day-num muted'}>{faults}</span>
                <span className="report-day-num report-day-clean">{clean}</span>
              </button>
            </h2>
            <div className="report-day-runs" hidden={!open}>
              {g.rows.map((run) => (
                <div key={run.id} className="report-run">
                  <span className="report-run-time">{formatVenueTime(run.started_at)}</span>
                  <span className="report-run-pc">{run.pc_name ?? '—'}</span>
                  <span className="report-run-exp">{run.experience_name ?? '—'}</span>
                  <div className="report-run-body">{renderBody(run)}</div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

// The manager feed cut down to what operations.fn_report_list_runs returns:
// no test launches, no runtime or billing columns, no reviewer name. Keep the
// two in step when either changes.
export function toReporterRuns(rows: BillingRun[]): ReporterRun[] {
  return rows
    .filter((r) => r.is_billable)
    .map((r) => {
      let report = r.report;
      if (report) {
        report = { ...report };
        delete report.reviewed_by_name;
      }
      return {
        id: r.id,
        started_at: r.started_at,
        pc_name: r.pc_name,
        experience_name: r.experience_name,
        report,
      };
    });
}
